import { type FormEvent, type SyntheticEvent, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useUISpeedProfilesCreateSpeedProfile } from '../../api/speed-profiles/speed-profiles';

type AddSpeedProfileFormProps = {
  onSuccess: () => void;
  onClose: () => void;
};

const USOC_PATTERN = /^[a-zA-Z0-9]+$/;
const MAX_USOC_LENGTH = 32;
const MAX_PROFILE_NAME_LENGTH = 32;

export const AddSpeedProfileForm = ({ onSuccess, onClose }: AddSpeedProfileFormProps) => {
  const [profileName, setProfileName] = useState('');
  const [usocs, setUsocs] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [formError, setFormError] = useState('');

  const { mutate: createSpeedProfile, isPending: isCreating } = useUISpeedProfilesCreateSpeedProfile();

  const handleProfileNameChange = (value: string) => {
    // Allow alphanumeric, spaces, hyphens, underscores for profile name
    setProfileName(value.slice(0, MAX_PROFILE_NAME_LENGTH));
  };

  const validateUsoc = (value: string): string | null => {
    if (!value.trim()) {
      return 'USOC code cannot be empty';
    }
    if (value.length > MAX_USOC_LENGTH) {
      return `USOC code must be ${MAX_USOC_LENGTH} characters or less`;
    }
    if (!USOC_PATTERN.test(value)) {
      return 'USOC code must contain only alphanumeric characters';
    }
    if (usocs.includes(value.toUpperCase())) {
      return `USOC "${value}" is already added`;
    }
    return null;
  };

  const handleUsocChange = (_event: SyntheticEvent, newValue: string[]) => {
    const validatedUsocs: string[] = [];
    for (const value of newValue) {
      const normalized = value.toUpperCase().trim();
      if (normalized && USOC_PATTERN.test(normalized) && normalized.length <= MAX_USOC_LENGTH) {
        if (!validatedUsocs.includes(normalized)) {
          validatedUsocs.push(normalized);
        }
      }
    }
    setUsocs(validatedUsocs);
    setFormError('');
  };

  const handleInputChange = (_event: SyntheticEvent, newInputValue: string) => {
    const sanitized = newInputValue.replace(/[^a-zA-Z0-9]/g, '');
    setInputValue(sanitized);
  };

  const addUsoc = (value: string) => {
    const normalized = value.toUpperCase().trim();
    if (!normalized) {
      return false;
    }
    const error = validateUsoc(normalized);
    if (error) {
      setFormError(error);
      return false;
    }
    setUsocs([...usocs, normalized]);
    setInputValue('');
    setFormError('');
    return true;
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Add tag on Enter, comma, or space
    if ((event.key === 'Enter' || event.key === ',' || event.key === ' ') && inputValue) {
      event.preventDefault();
      addUsoc(inputValue);
    }
  };

  const handleBlur = () => {
    // Add any remaining input when field loses focus
    if (inputValue) {
      addUsoc(inputValue);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!profileName.trim()) {
      setFormError('Speed profile name is required');
      return;
    }

    createSpeedProfile(
      {
        data: {
          groupname: profileName.trim(),
          input_value: '0',
          output_value: '0',
          usocs: usocs.length > 0 ? usocs : undefined,
        },
      },
      {
        onSuccess: () => {
          onSuccess();
        },
        onError: (error) => {
          if (error.response?.status === 409) {
            setFormError(`Speed profile "${profileName}" already exists`);
          } else if (error.response?.data?.detail) {
            setFormError(error.response.data.detail);
          } else {
            setFormError('Failed to create speed profile. Please try again.');
          }
        },
      },
    );
  };

  const handleClose = () => {
    setProfileName('');
    setUsocs([]);
    setInputValue('');
    setFormError('');
    onClose();
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogTitle>Add Speed Profile</DialogTitle>
      <DialogContent>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            required
            fullWidth
            label="Speed Profile Name"
            placeholder="Enter speed profile name"
            value={profileName}
            onChange={(e) => handleProfileNameChange(e.target.value)}
            helperText={`${profileName.length}/${MAX_PROFILE_NAME_LENGTH} characters`}
            slotProps={{ htmlInput: { maxLength: MAX_PROFILE_NAME_LENGTH } }}
          />
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={usocs}
            inputValue={inputValue}
            onChange={handleUsocChange}
            onInputChange={handleInputChange}
            renderValue={(value, getItemProps) =>
              value.map((option, index) => {
                const { key, ...itemProps } = getItemProps({ index });
                return <Chip key={key} label={option} size="small" {...itemProps} />;
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="USOC Codes (optional)"
                placeholder={
                  usocs.length === 0 ? 'Type USOC codes (Enter, comma, or space to add)' : 'Add more...'
                }
                helperText="Alphanumeric only, max 32 characters per code"
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                slotProps={{
                  htmlInput: {
                    ...params.inputProps,
                    maxLength: MAX_USOC_LENGTH,
                  },
                }}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isCreating}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={isCreating || !profileName.trim()}>
          {isCreating ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </form>
  );
};
